<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/tasks';
    import { computed, watch } from 'vue';
    import { useDashboardStore } from '@/stores/dashboard';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import axios from 'axios';
    import CreateTask from './CreateTask.vue';
    import CreateTaskFromTemplate from './CreateTaskFromTemplate.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import HoverInfo from '@/components/HoverInfo.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { Company, SharedData } from '@/types';

    const page = usePage<SharedData>();
    const props = defineProps<{
        company: Company;
    }>();

    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    const dashboardStore = useDashboardStore();

    const companyId = computed(() => props.company.id);
    const isClient = computed(() => page.props.auth.user.is_client);

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.DASHBOARD_TASKS, companyId],
        queryFn: getTasks,
    });

    async function getTasks() {
        const { data } = await axios.get(route('v2.dashboard.tasks.get'));
        return data.data;
    }

    watch(data, (newVal) => {
        if (newVal) {
            dashboardStore.setTasks(newVal);
        }
    });
</script>

<template>
    <PageCard class="mb-0 overflow-hidden md:mr-0 md:mb-1.5">
        <DataTable
            :table-key="QueryKey.DASHBOARD_TASKS"
            :columns="columns"
            :data="data"
            :loading="isPending"
            :default-column-visibility="{
                task_responsible_ids: false,
            }"
            :default-sorting="[
                {
                    id: 'deadline',
                    desc: false,
                },
            ]"
            :default-pagination="{
                pageIndex: 0,
                pageSize: 10,
            }"
            :enable-search="false"
            :enable-filters="false"
        >
            <template #title>
                <CardTitle class="flex items-center gap-2">
                    {{ $t('dashboards.tasks_header') }}
                    <HoverInfo
                        v-if="isClient"
                        :content="t('dashboards.tasks_help')"
                    />
                </CardTitle>
            </template>

            <template
                #actions
                v-if="hasPermission(Permission.DASHBOARD_ADMIN_CREATE)"
            >
                <DataTableToolbarActions name="dashboard-tasks">
                    <CreateTask :company="company" />
                    <CreateTaskFromTemplate :company="company" />
                </DataTableToolbarActions>
            </template>
        </DataTable>
    </PageCard>
</template>
